import React from 'react';
import PropTypes from 'prop-types';
import UserGroupController from '../../controllers/UserGroupController';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import { Add } from '@material-ui/icons';
import { makeStyles, Grid, Button, TextField, Dialog, DialogContent, DialogActions, DialogTitle } from '@material-ui/core';
import { SearchCardMini } from '../../components/Common/SearchCardMini';
import { isNullOrUndefined } from '../../helpers/Utils';
import { LoadingOverlay } from '../../components/Common/LoadingOverlay';
import { Alert } from '../../components/Common/Alert';
import UsersGroupsAddEdit from './UsersGroupsAddEdit';

const useStyles = makeStyles(theme => ({
    wrapper: {
        padding: '16px 32px',
        [theme.breakpoints.down('xs')]: {
            padding: 16,
        },
    },
    topArea: {
        paddingBottom: 8,
        '& .MuiButtonBase-root': {
            height: '100%',
        },
    },
}));

function UsersGroups(props) {
    const { Auth, PushHistory } = props;
    const [userGroups, setUserGroups] = React.useState([]);
    const [nameSearch, setNameSearch] = React.useState('');
    const [deleteRef, setDeleteRef] = React.useState(null);
    const [addEditRef, setAddEditRef] = React.useState(null);
    const [redirectUrl, setRedirectUrl] = React.useState(null);
    const [warning, setWarning] = React.useState(null);
    const [loading, setLoading] = React.useState(true);
    const classes = useStyles();

    const loadUserGroups = React.useCallback(async () => {
        setLoading(true);
        const response = await UserGroupController.getUserGroups(Auth.companyId);
        if (response.hasError) {
            setWarning(response.data);
        } else {
            setUserGroups(response.data);
        }
        setLoading(false);
    }, [Auth]);

    // init & auth check
    React.useEffect(() => {
        // ensure user is signed in
        const { isAuthenticated, isLoggingIn } = Auth;
        if (!isAuthenticated && !isLoggingIn) {
            setRedirectUrl('/Login');
        }
        // fetch user groups for inital render
        async function init() {
            await loadUserGroups();
        }
        init();
    }, [Auth, loadUserGroups]);

    // redirect
    React.useEffect(() => {
        if (!isNullOrUndefined(redirectUrl)) {
            PushHistory(redirectUrl);
        }
    }, [redirectUrl, PushHistory]);

    function handleUpdateUserGroups(userGroup) {
        const { id } = userGroup;
        const exists = !isNullOrUndefined(userGroups.filter(e => e.id === id)[0]);
        if (exists) {
            setUserGroups(userGroups.map(e => (e.id === id ? userGroup : e)));
        } else {
            setUserGroups([...userGroups, userGroup]);
        }
    }

    async function handleDeleteUserGroup() {
        setLoading(true);
        setWarning(null);
        const { id } = deleteRef;
        const response = await UserGroupController.deleteUserGroup(Auth.companyId, id);
        if (response.hasError) {
            setWarning(response.data);
        } else {
            setUserGroups(userGroups.filter(e => e.id !== id));
        }
        setDeleteRef(null);
        setLoading(false);
    }

    function handleInput(event) {
        const { value, name } = event.target;
        switch (name) {
            case 'name':
                setNameSearch(value);
                break;
            default:
                break;
        }
    }

    function buildTopArea() {
        return (
            <Grid container spacing={2} className={classes.topArea}>
                <Grid item xs="auto">
                    <Button variant="contained" color="primary" startIcon={<Add />} onClick={() => setAddEditRef({})}>
                        Add Group
                    </Button>
                </Grid>
                <Grid item lg={4} md={6} sm={12} xs={12}>
                    <TextField label="Search by Name" variant="filled" value={nameSearch} onChange={handleInput} name="name" fullWidth />
                </Grid>
                <Grid item xs={12}>
                    <Alert header="Something went wrong!" text={warning} />
                </Grid>
            </Grid>
        );
    }

    function buildUserGroup(userGroup) {
        const { id, name } = userGroup;
        return (
            <Grid key={id} item lg={2} md={3} sm={4} xs={12}>
                <SearchCardMini title={name}>
                    <Button onClick={() => setDeleteRef(userGroup)}>Delete</Button>
                    <Button onClick={() => setAddEditRef(userGroup)}>Edit</Button>
                    <Button onClick={() => setRedirectUrl('/Users?userGroup=' + userGroup.id)}>View Users</Button>
                </SearchCardMini>
            </Grid>
        );
    }

    function buildUsers() {
        if (isNullOrUndefined(userGroups)) {
            return null;
        }

        return (
            <Grid container spacing={2}>
                {userGroups.filter(e => e.name.includes(nameSearch)).map(buildUserGroup)}
            </Grid>
        );
    }

    function buildConfirmDelete() {
        return (
            <Dialog open={!isNullOrUndefined(deleteRef)} onClose={() => setDeleteRef(null)}>
                <DialogTitle>Delete User Group</DialogTitle>
                <DialogContent>Are you sure you want to perform this action?</DialogContent>
                <DialogActions>
                    <Button color="primary" onClick={() => handleDeleteUserGroup()}>
                        Yes
                    </Button>
                    <Button color="primary" onClick={() => setDeleteRef(null)}>
                        No
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }

    return (
        <div className={classes.wrapper}>
            <LoadingOverlay loading={loading} />
            {buildTopArea()}
            {buildUsers()}
            {buildConfirmDelete()}
            <UsersGroupsAddEdit
                open={!isNullOrUndefined(addEditRef)}
                onClose={() => setAddEditRef(null)}
                onError={setWarning}
                onDone={handleUpdateUserGroups}
                userGroup={addEditRef}
                companyId={Auth.companyId}
            />
        </div>
    );
}

const mapStateToProps = state => ({
    Auth: state.Authentication,
});

const mapDispatchToProps = dispatch => ({
    PushHistory: data => dispatch(push(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(UsersGroups);

UsersGroups.propTypes = {
    Auth: PropTypes.object,
    PushHistory: PropTypes.func,
};
